import { useCallback, useEffect, useRef, useState } from 'react'

import { SmepEvent, SmepEventConfig } from '@nativewaves/nw-cloud-sdk/smep'
import { useQueryClient } from '@tanstack/react-query'
import { useSnackbar } from 'notistack'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { useEventUpdateMutation } from 'hooks/mutations/smep'
import { eventQueryKeys } from 'services/queryKeys'

import { newId } from './ids'
import { parseConfig, serializeConfig } from './model'
import type { ConfigFormValues } from './types'

export type UseConfigFormProps = {
  event: SmepEvent
  config: SmepEventConfig
}

export const useConfigForm = ({ event, config }: UseConfigFormProps) => {
  const { t } = useTranslation(['domain'])
  const { enqueueSnackbar } = useSnackbar()
  const queryClient = useQueryClient()

  const eventId = event.id

  const formMethods = useForm<ConfigFormValues>({ defaultValues: parseConfig(config) })
  const { mutateAsync, isPending } = useEventUpdateMutation()

  const [externalChange, setExternalChange] = useState(false)
  const lastConfigRef = useRef(config)

  // Adopt server truth when the underlying config changes (e.g. saved from the
  // JSON tab). Never silently discard in-progress edits — surface a banner instead.
  useEffect(() => {
    const next = event.config
    if (!next || next === lastConfigRef.current) {
      return
    }
    lastConfigRef.current = next
    if (formMethods.formState.isDirty) {
      setExternalChange(true)
    } else {
      formMethods.reset(parseConfig(next))
    }
  }, [event.config, formMethods])

  const reloadFromServer = useCallback(() => {
    if (event.config) {
      formMethods.reset(parseConfig(event.config))
    }
    setExternalChange(false)
  }, [event.config, formMethods])

  const handleSubmit = formMethods.handleSubmit(async (values) => {
    // Merge into the freshest server config so a concurrent JSON-tab change to an
    // unsupported field is preserved (no clobber).
    const fresh = await queryClient.ensureQueryData(eventQueryKeys.detail({ eventId }))
    const payload = serializeConfig(fresh.config ?? config, values, newId)

    const updated = await mutateAsync({ eventId, smepEventEdit: { config: payload } })

    if (updated.config) {
      lastConfigRef.current = updated.config
      formMethods.reset(parseConfig(updated.config), { keepSubmitCount: true })
    }
    setExternalChange(false)
    enqueueSnackbar(t('domain:Workspace.Event.ConfigEditor.saved', { name: updated.name }), { variant: 'success' })
  })

  return { formMethods, handleSubmit, isSaving: isPending, externalChange, reloadFromServer }
}
